import { useEffect, useRef } from "react"

import CheckoutModal from "./checkout_modal/index"
import MobileWarning from "./mobile_warning/index"
import ProUpgradeModal from "./pro_upgrade/index"
import modalCopy, { CheckoutStep } from "./modal_copy"
import { trackSubscriptionPaywallShown } from "app/utils/mixpanel_tracking"
import { isPaidTier, tierDisplayName } from "shared/utils/tiers"

export interface URLs {
  modalSubscriptionUrl: string
  newProSubscriptionUrl: string
  newProPlusSubscriptionUrl: string
}
export interface User {
  eligibleForTrial: boolean
  isTrialing: boolean
  eligibleTrialPeriod: number
  subscriptionPaymentType: string
  tier: string
}

type PaywallLocation =
  | "Applet Config"
  | "Applet Details"
  | "AI Applet Creator"
  | "Classic Applet Creator"
  | "Home"
  | "My Applets"
  | "Plans"
  | "Service"
  | "Connect"
  | "Settings"

type PaywallReason =
  | "Applet Features"
  | "Applet Quota"
  | "Applet Quota Upsell"
  | "Rate Limit"
  | "Onboarding"
  | "Banner"
  | "Visit"

interface Props {
  appletFeatureFlags: {
    intermediate_pro_features: boolean
    pro_features: boolean
  }
  initialPromoCode: string
  onClose: () => void
  onUpgrade: (checkoutModalTarget: string) => void
  premiumService?: string
  proPlusProductId: string
  step: CheckoutStep
  tierRequirement: string
  trackLocation?: PaywallLocation
  trackReason?: PaywallReason
  urls: URLs
  user: User
  visible: boolean
}

// Map checkout step to paywall reason (category)
const getPaywallReason = (step: CheckoutStep): PaywallReason => {
  switch (step) {
    case "applet_quota":
      return "Applet Quota"
    case "applet_quota_upsell":
      return "Applet Quota Upsell"
    case "ai_maker_rate_limit":
      return "Rate Limit"
    case "applet_features":
    case "action":
    case "trigger":
    case "queries":
    case "filter_code":
    case "multi_action":
    case "multi_service_account":
    case "convert_pro_plus_applet":
    case "pro_service_tqas":
      return "Applet Features"
    case "default":
    case "default_pro":
    case "plan_upgrade":
    default:
      return "Visit"
  }
}

const UpgradePlanModal = (props: Props) => {
  const { appletFeatureFlags, onClose, premiumService, proPlusProductId, step, tierRequirement, trackLocation, trackReason, user, visible } = props
  const canUpgrade = user.subscriptionPaymentType === "stripe"
  const upgradingExistingSubscription = isPaidTier(user.tier)
  const hasTrackedRef = useRef(false)

  const modalCopyObj = modalCopy({
    appletFeatureFlags,
    currentTier: user.tier,
    eligibleForTrial: user.eligibleForTrial,
    eligibleTrialPeriod: user.eligibleTrialPeriod,
    isTrialing: user.isTrialing,
    premiumService,
    step,
    tierRequirement,
  })

  useEffect(() => {
    if (visible && !hasTrackedRef.current && trackLocation) {
      hasTrackedRef.current = true
      const targetTier = tierDisplayName(modalCopyObj.checkoutModalTarget)
      trackSubscriptionPaywallShown({
        location: trackLocation,
        reason: trackReason || getPaywallReason(step),
        targetTier,
      })
    }
    if (!visible) {
      hasTrackedRef.current = false
    }
  }, [visible, step, trackLocation, trackReason, modalCopyObj.checkoutModalTarget])

  return upgradingExistingSubscription ? (
    canUpgrade ? (
      <ProUpgradeModal
        modalCopyObj={modalCopyObj}
        onClose={onClose}
        onUpgrade={props.onUpgrade}
        proPlusProductId={proPlusProductId}
        step={step}
        trackLocation={trackLocation}
        user={user}
        visible={visible}
      />
    ) : (
      <MobileWarning onClose={onClose} visible={visible} />
    )
  ) : (
    <CheckoutModal {...props} modalCopyObj={modalCopyObj} />
  )
}

export default UpgradePlanModal
